/**
 * Ravinaaz Theme Wishlist Module
 * Handles local storage, card hover toggles, header counters, slide-out drawer, and dynamic 404 page fallback.
 */

(function () {
  'use strict';

  const WISHLIST_KEY = 'ravinaaz_wishlist';

  window.RavinaazWishlist = {
    get() {
      try {
        const data = localStorage.getItem(WISHLIST_KEY);
        return data ? JSON.parse(data) : [];
      } catch (e) {
        console.error('Error reading wishlist from localStorage', e);
        return [];
      }
    },

    save(items) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
        document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
      } catch (e) {
        console.error('Error saving wishlist to localStorage', e);
      }
    },

    exists(idOrHandle) {
      if (!idOrHandle) return false;
      const items = this.get();
      const targetStr = String(idOrHandle);
      return items.some(item => String(item.id) === targetStr || item.handle === targetStr);
    },

    toggle(product) {
      if (!product || (!product.id && !product.handle)) return false;
      let items = this.get();
      const targetId = String(product.id || product.handle);
      const index = items.findIndex(item => String(item.id) === targetId || item.handle === targetId);
      
      let isAdded = false;
      if (index > -1) {
        items.splice(index, 1);
        isAdded = false;
      } else {
        items.push({
          id: String(product.id || product.handle),
          handle: product.handle || '',
          title: product.title || '',
          price: product.price || '',
          compare_at_price: product.compare_at_price || '',
          image: product.image || '',
          url: product.url || (product.handle ? `/products/${product.handle}` : '#'),
          vendor: product.vendor || '',
          variantId: product.variantId || ''
        });
        isAdded = true;
      }

      this.save(items);
      return isAdded;
    },

    remove(idOrHandle) {
      let items = this.get();
      const targetStr = String(idOrHandle);
      items = items.filter(item => String(item.id) !== targetStr && item.handle !== targetStr);
      this.save(items);
    },

    clear() {
      this.save([]);
    },

    count() {
      return this.get().length;
    },

    updateBadges() {
      const count = this.count();
      const badges = document.querySelectorAll('.js-wishlist-count');
      badges.forEach(badge => {
        const span = badge.querySelector('span[aria-hidden]') || badge;
        span.textContent = count;
        if (count > 0) {
          badge.classList.remove('is-empty');
          badge.style.display = badge.classList.contains('cart-count-bubble') ? 'flex' : 'inline-flex';
        } else {
          badge.classList.add('is-empty');
          badge.style.display = 'none';
        }
      });
    },

    updateButtons() {
      const btns = document.querySelectorAll('.js-wishlist-btn');
      btns.forEach(btn => {
        const id = btn.dataset.productId || btn.dataset.productHandle;
        const textSpan = btn.querySelector('.rz-pdp-wishlist-text');
        if (this.exists(id)) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-label', 'Remove from Wishlist');
          btn.setAttribute('title', 'Remove from Wishlist');
          if (textSpan) textSpan.textContent = 'Remove from Wishlist';
        } else {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-label', 'Add to Wishlist');
          btn.setAttribute('title', 'Add to Wishlist');
          if (textSpan) textSpan.textContent = 'Add to Wishlist';
        }
      });
    }
  };

  // Drawer Open / Close Helpers
  window.openWishlistDrawer = function () {
    const drawer = document.getElementById('rz-wishlist-drawer');
    if (!drawer) return;
    window.renderWishlistDrawer();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  window.closeWishlistDrawer = function () {
    const drawer = document.getElementById('rz-wishlist-drawer');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  // Render Wishlist Drawer Items
  window.renderWishlistDrawer = function () {
    const itemsContainer = document.getElementById('wishlist-drawer-items');
    const emptyView = document.getElementById('wishlist-drawer-empty');
    if (!itemsContainer) return;

    const items = window.RavinaazWishlist.get();

    if (items.length === 0) {
      itemsContainer.innerHTML = '';
      itemsContainer.style.display = 'none';
      if (emptyView) emptyView.style.display = 'block';
      return;
    }

    if (emptyView) emptyView.style.display = 'none';
    itemsContainer.style.display = 'flex';

    itemsContainer.innerHTML = items.map(item => `
      <div class="wishlist-drawer-item" data-id="${item.id}">
        <img src="${item.image || '//cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'}" alt="${item.title}" class="wishlist-drawer-item__img" />
        <div class="wishlist-drawer-item__info">
          <h4 class="wishlist-drawer-item__title">
            <a href="${item.url}">${item.title}</a>
          </h4>
          <div class="wishlist-drawer-item__price">${item.price}</div>
        </div>
        <button type="button" class="wishlist-drawer-item__remove js-wishlist-drawer-remove" data-id="${item.id}" aria-label="Remove item">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `).join('');

    // Remove item listener in drawer
    itemsContainer.querySelectorAll('.js-wishlist-drawer-remove').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const id = this.dataset.id;
        window.RavinaazWishlist.remove(id);
      });
    });
  };

  // Global click handler for wishlist buttons & drawer triggers
  document.addEventListener('click', function (e) {
    // Drawer close buttons
    const closeBtn = e.target.closest('.js-wishlist-drawer-close');
    if (closeBtn) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      window.closeWishlistDrawer();
      return;
    }

    // Wishlist card / PDP button click
    const btn = e.target.closest('.js-wishlist-btn');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const productData = {
      id: btn.dataset.productId,
      handle: btn.dataset.productHandle,
      title: btn.dataset.productTitle,
      price: btn.dataset.productPrice,
      compare_at_price: btn.dataset.productComparePrice,
      image: btn.dataset.productImage,
      url: btn.dataset.productUrl,
      vendor: btn.dataset.productVendor,
      variantId: btn.dataset.variantId
    };

    const isAdded = window.RavinaazWishlist.toggle(productData);

    // Visual feedback pop effect
    btn.classList.add('rz-heart-pop');
    setTimeout(() => btn.classList.remove('rz-heart-pop'), 400);

    window.RavinaazWishlist.updateButtons();
    window.RavinaazWishlist.updateBadges();

    // Trigger toast message
    if (window.showWishlistToast) {
      const title = productData.title || 'Product';
      const msg = isAdded ? `"${title}" added to Wishlist` : `"${title}" removed from Wishlist`;
      window.showWishlistToast(msg, isAdded);
    }
  }, { capture: true });

  // Listen for sync events
  document.addEventListener('wishlist:updated', function () {
    window.RavinaazWishlist.updateButtons();
    window.RavinaazWishlist.updateBadges();
    
    // Re-render drawer if open
    if (document.getElementById('rz-wishlist-drawer')?.classList.contains('is-open')) {
      window.renderWishlistDrawer();
    }

    // If on wishlist page, re-render
    if (document.getElementById('wishlist-grid-container')) {
      window.renderWishlistPage();
    }
  });

  // Render Wishlist Page Function
  window.renderWishlistPage = function () {
    const container = document.getElementById('wishlist-grid-container');
    const emptyView = document.getElementById('wishlist-empty-state');
    const countHeader = document.getElementById('wishlist-total-count');
    const clearBtn = document.getElementById('wishlist-clear-all-btn');

    if (!container) return;

    const items = window.RavinaazWishlist.get();

    if (countHeader) {
      countHeader.textContent = `(${items.length} ${items.length === 1 ? 'item' : 'items'})`;
    }

    if (clearBtn) {
      clearBtn.style.display = items.length > 0 ? 'inline-block' : 'none';
    }

    if (items.length === 0) {
      container.innerHTML = '';
      container.style.display = 'none';
      if (emptyView) emptyView.style.display = 'block';
      return;
    }

    if (emptyView) emptyView.style.display = 'none';
    container.style.display = 'grid';

    container.innerHTML = items.map(item => {
      const hasDiscount = item.compare_at_price && item.compare_at_price !== item.price;
      return `
        <div class="wishlist-card product-card-wrapper" data-wishlist-id="${item.id}">
          <div class="wishlist-card__image-wrapper">
            <a href="${item.url}" class="wishlist-card__link">
              <img src="${item.image || '//cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'}" alt="${item.title}" loading="lazy" class="wishlist-card__image" />
            </a>
            <button type="button" class="wishlist-card__remove-btn js-wishlist-remove" data-id="${item.id}" aria-label="Remove item">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="wishlist-card__info">
            ${item.vendor ? `<span class="wishlist-card__vendor">${item.vendor}</span>` : ''}
            <h3 class="wishlist-card__title">
              <a href="${item.url}">${item.title}</a>
            </h3>
            <div class="wishlist-card__price">
              <span class="wishlist-card__price-regular">${item.price}</span>
              ${hasDiscount ? `<span class="wishlist-card__price-compare">${item.compare_at_price}</span>` : ''}
            </div>
            <div class="wishlist-card__actions">
              <a href="${item.url}" class="wishlist-card__btn button button--primary">View Product</a>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Attach listeners for remove buttons on wishlist page
    container.querySelectorAll('.js-wishlist-remove').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const id = this.dataset.id;
        window.RavinaazWishlist.remove(id);
      });
    });
  };

  // Toast Notification helper
  window.showWishlistToast = function (message, isAdded) {
    let toast = document.getElementById('rz-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'rz-wishlist-toast';
      toast.className = 'rz-wishlist-toast';
      document.body.appendChild(toast);
    }
    toast.innerHTML = `
      <div class="rz-wishlist-toast__content">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="${isAdded ? '#b8860b' : 'none'}" stroke="${isAdded ? '#b8860b' : 'currentColor'}" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
        <span>${message}</span>
        <a href="/pages/wishlist" class="rz-wishlist-toast__link">View Wishlist &rarr;</a>
      </div>
    `;
    toast.classList.add('is-visible');

    if (window.rzToastTimer) clearTimeout(window.rzToastTimer);
    window.rzToastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 3500);
  };

  // 404 URL Interceptor for /pages/wishlist
  function handleWishlistUrlFallback() {
    const currentPath = window.location.pathname.toLowerCase();
    if (currentPath.includes('/pages/wishlist') || currentPath.endsWith('/wishlist')) {
      const mainContent = document.getElementById('MainContent') || document.body;
      const is404 = document.querySelector('.rz-404-container, .template-404');
      
      if (is404 || !document.getElementById('wishlist-grid-container')) {
        document.title = 'My Wishlist — Ravinaaz';
        mainContent.innerHTML = `
          <div class="wishlist-section color-scheme-1 gradient">
            <div class="page-width">
              <div class="wishlist-header">
                <h1 class="wishlist-header__title">
                  My Wishlist
                  <span id="wishlist-total-count" class="wishlist-header__count"></span>
                </h1>
                <div class="wishlist-header__actions">
                  <button type="button" id="wishlist-clear-all-btn" class="wishlist-clear-btn" style="display: none;">
                    Clear Wishlist
                  </button>
                </div>
              </div>
              <div id="wishlist-grid-container" class="wishlist-grid"></div>
              <div id="wishlist-empty-state" class="wishlist-empty" style="display: none;">
                <div class="wishlist-empty__icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="64" height="64" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                </div>
                <h2 class="wishlist-empty__title">Your Wishlist is Empty</h2>
                <p class="wishlist-empty__text">You haven't added any items to your wishlist yet. Explore our handcrafted jewelry collections and save your favorite pieces.</p>
                <a href="/collections/all" class="button button--primary">Explore Collections</a>
              </div>
            </div>
          </div>
        `;
        window.renderWishlistPage();
      }
    }
  }

  // Initial Sync on DOM Ready
  document.addEventListener('DOMContentLoaded', function () {
    window.RavinaazWishlist.updateButtons();
    window.RavinaazWishlist.updateBadges();

    // Check for 404 fallback on /pages/wishlist URL
    handleWishlistUrlFallback();

    if (document.getElementById('wishlist-grid-container')) {
      window.renderWishlistPage();

      const clearBtn = document.getElementById('wishlist-clear-all-btn');
      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          if (confirm('Are you sure you want to clear your entire wishlist?')) {
            window.RavinaazWishlist.clear();
          }
        });
      }
    }

    const drawerClearBtn = document.getElementById('wishlist-drawer-clear-btn');
    if (drawerClearBtn) {
      drawerClearBtn.addEventListener('click', function () {
        if (confirm('Are you sure you want to clear all wishlist items?')) {
          window.RavinaazWishlist.clear();
        }
      });
    }
  });
})();
